import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { IChangeAgentCheckItem, IChangeAgentCheckParam, IChangeAgentParam, IChangeAgentRecord, IUpdateAgentConfigConfirm, IUpdateAgentConfigItem } from '@core/main/types';

const hostUrl = getHostbaseUrl();

/**
 * 账户管理 - 更换代理商等级 - 获取分页列表
 */
export async function queryAccAgentLevelPage(params: IChangeAgentParam) {
  let total = 0;
  let data = [] as IChangeAgentRecord[];

  try {
    const { code, context, message } = await Http.post<{ data: IChangeAgentRecord[]; total: number }>(
      `${hostUrl}/side/change/agent-belong/page`,
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IChangeAgentRecord[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户管理 - 更换代理商等级 - 导出
 */
export async function exportAccAgentLevel(params: IChangeAgentParam) {
  let buffer = null as Blob | null;
  let fileName = '';

  try {
    const { code, context, message, headers } = await Http.get<Blob>(
      `${hostUrl}/side/change/agent-belong/page-export`,
      params,
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);
      
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 账户管理 - 更换代理商等级 - 企业变更代理商 - 校验
 */
export async function queryAgentChangeLevelCheck(params: IChangeAgentCheckParam) {
  let data = [] as IChangeAgentCheckItem[];

  try {
    const { code, context, message } = await Http.post<{ checkItems: IChangeAgentCheckItem[] }>(
      `${hostUrl}/side/change/agent-belong/change-level-check`,
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { checkItems: IChangeAgentCheckItem[] };

      data = res.checkItems || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户管理 - 更换代理商等级 - 查询代理商直属子代理商数量
 */
export async function queryAgentSubAgentCount(agentId: number) {
  let data = 0;

  try {
    const { code, context, message } = await Http.get<number>(`${hostUrl}/side/change/agent-belong/sub-agent-count`, { agentId });

    if (code === EAxiosResponseCode.Succeed) {
      data = context || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户管理 - 升级为特代 - 校验
 */
export async function queryAccUpdateAgentCheck(agentId: number) {
  let data = [] as IChangeAgentCheckItem[];

  try {
    const { code, context, message } = await Http.post<{ checkItems: IChangeAgentCheckItem[] }>(
      `${hostUrl}/side/change/agent-belong/upgrade/check`,
      { agentId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { checkItems: IChangeAgentCheckItem[] };

      data = res.checkItems || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户管理 - 升级为特代 - 产品列表
 */
export async function queryAccUpdateProductList(agentId: number) {
  let data = [] as IUpdateAgentConfigItem[];

  try {
    const { code, context, message } = await Http.get<IUpdateAgentConfigItem[]>(
      `${hostUrl}/side/change/agent-belong/upgrade/auth-list`,
      { agentId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as IUpdateAgentConfigItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户管理 - 升级为特代 - 确认变更
 */
export async function queryAccUpdateConfirm(params: IUpdateAgentConfigConfirm) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/change/agent-belong/upgrade/confirm`,
      params,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户管理 - 升级特代 - 获取分页列表
 */
export async function queryAccUpdateTopAgentPage(params: IChangeAgentParam) {
  let total = 0;
  let data = [] as IChangeAgentRecord[];

  try {
    const { code, context, message } = await Http.post<{ data: IChangeAgentRecord[]; total: number }>(
      `${hostUrl}/side/change/agent-belong/upgrade/page`,
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IChangeAgentRecord[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户管理 - 升级特代 - 导出
 */
export async function exportAccUpdateTopAgent(params: IChangeAgentParam) {
  let buffer = null as Blob | null;
  let fileName = '';

  try {
    const { code, context, message, headers } = await Http.get<Blob>(
      `${hostUrl}/side/change/agent-belong/upgrade/page-export`,
      params,
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);
      
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}
